import React, { useState, useEffect, useRef, useCallback } from 'react';
import './App.css';
// Silently storing the flag in the window object
// @ts-ignore
window.flag = "CreekCTF{1nsp3ct_3l3m3nt_m4st3r}";

const App: React.FC = () => {
  const [gameStarted, setGameStarted] = useState(false);
  const [score, setScore] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  
  const boardRef = useRef<HTMLDivElement>(null);
  const ballRef = useRef<HTMLDivElement>(null);
  const playerPaddleRef = useRef<HTMLDivElement>(null);
  const aiPaddleRef = useRef<HTMLDivElement>(null);
  
  // Use refs instead of state for position values to avoid re-renders
  const ballPositionRef = useRef({ x: 50, y: 50 });
  const ballDirectionRef = useRef({ x: 0.25, y: 0.25 }); // Start at 1/4 speed
  const speedMultiplierRef = useRef(1); // New ref for speed multiplier
  const playerPaddlePositionRef = useRef(50);
  const aiPaddlePositionRef = useRef(50);
  const requestIdRef = useRef<number>(0);
  const lastTimeRef = useRef<number>(0);
  const scoreRef = useRef(0);
  
  // Use state only for rendering
  const [ballPosition, setBallPosition] = useState({ x: 50, y: 50 });
  const [playerPaddlePosition, setPlayerPaddlePosition] = useState(50);
  const [aiPaddlePosition, setAiPaddlePosition] = useState(50);

  // Handle mouse movement for paddle control
  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!boardRef.current) return;
    
    const boardRect = boardRef.current.getBoundingClientRect();
    const relativeX = e.clientX - boardRect.left;
    const paddlePos = (relativeX / boardRect.width) * 100;
    
    // Keep paddle within board
    if (paddlePos >= 0 && paddlePos <= 100) {
      playerPaddlePositionRef.current = paddlePos;
    }
  }, []);

  // Helper function to normalize vector
  const normalizeVector = (vector: { x: number, y: number }) => {
    const magnitude = Math.sqrt(vector.x * vector.x + vector.y * vector.y);
    return {
      x: vector.x / magnitude,
      y: vector.y / magnitude
    };
  };

  // Helper function to get random angle within bounds
  const getRandomAngle = (baseAngle: number, spread: number) => {
    return baseAngle + (Math.random() * spread - spread / 2);
  };

  // Helper function to calculate reflection angle with dampening
  const calculateReflection = (ballVelocity: { x: number, y: number }, surfaceNormal: { x: number, y: number }, dampeningFactor = 1) => {
    const dot = ballVelocity.x * surfaceNormal.x + ballVelocity.y * surfaceNormal.y;
    return {
      x: (ballVelocity.x - 2 * dot * surfaceNormal.x) * dampeningFactor,
      y: ballVelocity.y - 2 * dot * surfaceNormal.y
    };
  };

  // Game loop with requestAnimationFrame for better performance
  const gameLoop = useCallback((timestamp: number) => {
    if (!gameStarted || gameOver) return;
    
    // Calculate delta time for consistent movement regardless of framerate
    if (!lastTimeRef.current) lastTimeRef.current = timestamp;
    const deltaTime = timestamp - lastTimeRef.current;
    lastTimeRef.current = timestamp;
    
    // Make sure we have all the necessary refs
    if (!boardRef.current || !ballRef.current || !playerPaddleRef.current || !aiPaddleRef.current) {
      requestIdRef.current = requestAnimationFrame(gameLoop);
      return;
    }
    
    const boardRect = boardRef.current.getBoundingClientRect();
    const ballRect = ballRef.current.getBoundingClientRect();
    const playerPaddleRect = playerPaddleRef.current.getBoundingClientRect();
    const aiPaddleRect = aiPaddleRef.current.getBoundingClientRect();
    
    // Update AI paddle position with smoother movement
    const targetPosition = ballPositionRef.current.x;
    const aiSpeed = 0.8 * (deltaTime / 16); // Slightly reduced AI speed for better gameplay
    const aiDiff = targetPosition - aiPaddlePositionRef.current;
    aiPaddlePositionRef.current += Math.sign(aiDiff) * Math.min(Math.abs(aiDiff), aiSpeed);
    
    // Update ball position with current velocity and speed multiplier
    const speedFactor = Math.max(1, deltaTime / 16) * speedMultiplierRef.current;
    const newX = ballPositionRef.current.x + ballDirectionRef.current.x * speedFactor;
    const newY = ballPositionRef.current.y + ballDirectionRef.current.y * speedFactor;
    
    // Wall collisions with dampening
    if (newX <= 0 || newX >= 100) {
      const wallNormal = { x: newX <= 0 ? 1 : -1, y: 0 };
      // Apply 70% dampening to horizontal momentum on wall hits
      const reflection = calculateReflection(ballDirectionRef.current, wallNormal, 0.7);
      // Ensure the ball maintains some horizontal movement
      reflection.x = Math.max(Math.min(reflection.x, 0.8), -0.8);
      ballDirectionRef.current = normalizeVector(reflection);
    }
    
    // AI paddle collision (top) with randomized reflection
    const ballTop = newY;
    const aiPaddleBottom = (aiPaddleRect.height / boardRect.height) * 100;
    const ballLeft = newX - (ballRect.width / boardRect.width) * 50;
    const ballRight = newX + (ballRect.width / boardRect.width) * 50;
    const aiPaddleLeft = aiPaddlePositionRef.current - (aiPaddleRect.width / boardRect.width) * 50;
    const aiPaddleRight = aiPaddlePositionRef.current + (aiPaddleRect.width / boardRect.width) * 50;
    
    if (ballTop <= aiPaddleBottom && 
        ballRight >= aiPaddleLeft && 
        ballLeft <= aiPaddleRight && 
        ballDirectionRef.current.y < 0) {
      // Base angle from hit position
      const hitPosition = (newX - aiPaddlePositionRef.current) / ((aiPaddleRect.width / boardRect.width) * 25);
      // Generate random angle between -60 and 60 degrees, influenced by hit position
      const angleInRadians = getRandomAngle(hitPosition * Math.PI / 6, Math.PI / 3);
      // Convert angle to direction vector
      ballDirectionRef.current = normalizeVector({
        x: Math.sin(angleInRadians),
        y: Math.cos(angleInRadians) // Positive for downward movement
      });
      
      // Increase speed by 2% after AI paddle hit
      speedMultiplierRef.current = Math.min(3, speedMultiplierRef.current * 1.02);
    }
    
    // Player paddle collision (bottom) with randomized reflection
    const ballBottom = newY + (ballRect.height / boardRect.height) * 100;
    const playerPaddleTop = 100 - (playerPaddleRect.height / boardRect.height) * 100 - 5;
    const playerPaddleLeft = playerPaddlePositionRef.current - (playerPaddleRect.width / boardRect.width) * 50;
    const playerPaddleRight = playerPaddlePositionRef.current + (playerPaddleRect.width / boardRect.width) * 50;
    
    if (ballBottom >= playerPaddleTop && 
        ballRight >= playerPaddleLeft && 
        ballLeft <= playerPaddleRight && 
        ballDirectionRef.current.y > 0) {
      // Base angle from hit position
      const hitPosition = (newX - playerPaddlePositionRef.current) / ((playerPaddleRect.width / boardRect.width) * 25);
      // Generate random angle between -60 and 60 degrees, influenced by hit position
      const angleInRadians = getRandomAngle(hitPosition * Math.PI / 6, Math.PI / 3);
      // Convert angle to direction vector
      ballDirectionRef.current = normalizeVector({
        x: Math.sin(angleInRadians),
        y: -Math.cos(angleInRadians) // Negative for upward movement
      });
      
      scoreRef.current += 1;
      setScore(scoreRef.current);
      // Increase speed by 2% after player paddle hit
      speedMultiplierRef.current = Math.min(3, speedMultiplierRef.current * 1.02);
    }
    
    // Game over conditions remain the same
    if (newY >= 100) {
      setGameOver(true);
      setScore(scoreRef.current);
      cancelAnimationFrame(requestIdRef.current);
      return;
    }
    
    // Ball passed AI paddle (player scores)
    if (newY <= 0) {
      const wallNormal = { x: 0, y: 1 };
      const reflection = calculateReflection(ballDirectionRef.current, wallNormal);
      ballDirectionRef.current = normalizeVector(reflection);
      scoreRef.current += 5;
      setScore(scoreRef.current);
    }
    
    // Update position refs
    ballPositionRef.current = { x: newX, y: newY };
    
    // Update state for rendering
    setBallPosition({ x: newX, y: newY });
    setPlayerPaddlePosition(playerPaddlePositionRef.current);
    setAiPaddlePosition(aiPaddlePositionRef.current);
    
    // Continue the game loop
    requestIdRef.current = requestAnimationFrame(gameLoop);
  }, [gameStarted, gameOver]);
  
  // Start/reset game
  const startGame = useCallback(() => {
    // Reset game state
    setGameStarted(true);
    setGameOver(false);
    setScore(0);
    scoreRef.current = 0;
    
    // Reset positions and speed with normalized initial direction
    ballPositionRef.current = { x: 50, y: 50 };
    const initialDirection = normalizeVector({ x: 0.25, y: 0.25 });
    ballDirectionRef.current = initialDirection;
    speedMultiplierRef.current = 1;
    playerPaddlePositionRef.current = 50;
    aiPaddlePositionRef.current = 50;
    
    // Reset timing reference
    lastTimeRef.current = 0;
    
    // Cancel any existing animation frame
    if (requestIdRef.current) {
      cancelAnimationFrame(requestIdRef.current);
      requestIdRef.current = 0;
    }
    
    // Start new game loop
    requestIdRef.current = requestAnimationFrame(gameLoop);
  }, [gameLoop]);

  // Handle click to start
  const handleBoardClick = useCallback(() => {
    if (!gameStarted || gameOver) {
      startGame();
    }
  }, [gameStarted, gameOver, startGame]);

  // Setup and cleanup effect
  useEffect(() => {
    if (gameStarted && !gameOver) {
      // Add mouse event listener
      document.addEventListener('mousemove', handleMouseMove);
      
      // Start game loop if not already running
      if (!requestIdRef.current) {
        console.log("Starting game loop from effect");
        requestIdRef.current = requestAnimationFrame(gameLoop);
      }
    }
    
    // Cleanup function
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      if (requestIdRef.current) {
        cancelAnimationFrame(requestIdRef.current);
        requestIdRef.current = 0;
      }
    };
  }, [gameStarted, gameOver, handleMouseMove, gameLoop]);

  return (
    <div className="min-h-screen">
      <div className="container">
        <h1>Pong Game</h1>
        
        <div className="game-container">
          <div 
            ref={boardRef} 
            className="game-board"
            onClick={handleBoardClick}
            style={{ cursor: (!gameStarted || gameOver) ? 'pointer' : 'none' }}
          >
            {gameStarted && !gameOver && (
              <>
                {/* AI Paddle (top) */}
                <div 
                  ref={aiPaddleRef}
                  className="paddle ai-paddle"
                  style={{ 
                    left: `${aiPaddlePosition}%`, 
                    top: '0', 
                    transform: 'translateX(-50%)' 
                  }}
                />
                
                {/* Ball */}
                <div 
                  ref={ballRef}
                  className="ball"
                  style={{ 
                    left: `${ballPosition.x}%`, 
                    top: `${ballPosition.y}%`
                  }}
                />
                
                {/* Player Paddle (bottom) */}
                <div 
                  ref={playerPaddleRef}
                  className="paddle player-paddle"
                  style={{ 
                    left: `${playerPaddlePosition}%`, 
                    bottom: '10px', 
                    transform: 'translateX(-50%)' 
                  }}
                />
              </>
            )}
            
            {!gameStarted && !gameOver && (
              <div className="center-content">
                <div className="start-message">
                  <h3>Ready to Play?</h3>
                  <p>Use your mouse to control the paddle.</p>
                  <div className="click-to-start">Click to Start</div>
                </div>
              </div>
            )}
            
            {gameOver && (
              <div className="center-content">
                <div className="game-over-message">
                  <h3>Game Over</h3>
                  <p>Your score: {score}</p>
                  <div className="click-to-start">Click to Play Again</div>
                </div>
              </div>
            )}
          </div>
          
          <div className="score-container">
            <div>Score: <span>{score}</span></div>
          </div>
          
          <div className="instructions">
            <h2>Instructions</h2>
            <ul>
              <li>Use your mouse to control the bottom paddle.</li>
              <li>Bounce the ball back and forth with the AI opponent.</li>
              <li>Score 1 point for each successful return.</li>
              <li>Get 5 bonus points when the ball passes the AI paddle!</li>
            </ul>
          </div>
        </div>
        
        <footer>
          <p>Simple Pong Game - v1.0</p>
        </footer>
      </div>
    </div>
  );
};

export default App;
